/**
 * Stores & Users analytics section.
 *
 * Reuses `GET /stores/statistics` + `GET /users/statistics` to show store
 * activity, user activity and the role distribution.
 */

import React from 'react';
import {useTranslation} from 'react-i18next';
import {Store} from 'lucide-react';
import AnalyticsSectionCard from '../AnalyticsSectionCard';
import MetricTile from '../MetricTile';
import MiniBarList from '../MiniBarList';
import ProgressRing from '../ProgressRing';
import StatusSplitBar from '../StatusSplitBar';
import type {StatusSegment} from '../../models/homeAnalytics.model';
import {useStoresUsersAnalytics} from '../../hooks/useHomeAnalytics';

interface StoresUsersSectionProps {
  enabled: boolean;
}

const StoresUsersSection: React.FC<StoresUsersSectionProps> = ({enabled}) => {
  const {t} = useTranslation();
  const {data, isLoading, isError} = useStoresUsersAnalytics(enabled);

  const storeSegments: StatusSegment[] = data
    ? [
        {
          label: t('analytics.stores_users.active'),
          value: data.activeStores,
          tone: 'success'
        },
        {
          label: t('analytics.stores_users.inactive'),
          value: data.inactiveStores,
          tone: 'error'
        },
        {
          label: t('analytics.stores_users.pending_payment'),
          value: data.pendingPaymentStores,
          tone: 'warning'
        }
      ]
    : [];

  return (
    <AnalyticsSectionCard
      title={t('analytics.stores_users.title')}
      icon={<Store className="w-5 h-5" />}
      isLoading={isLoading}
      isError={isError}
      className="xl:col-span-2"
    >
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-2">
              <MetricTile
                label={t('analytics.stores_users.total_stores')}
                value={data.totalStores}
                tone="primary"
              />
              <MetricTile
                label={t('analytics.stores_users.market_developers')}
                value={data.marketDevelopers}
                tone="success"
              />
              <MetricTile
                label={t('analytics.stores_users.low_stock_stores')}
                value={data.lowStockStores}
                tone="warning"
              />
              <MetricTile
                label={t('analytics.stores_users.out_of_stock_stores')}
                value={data.outOfStockStores}
                tone="error"
              />
            </div>
            <div>
              <h4 className="text-xs font-semibold text-gray-500 mb-2">
                {t('analytics.stores_users.store_status')}
              </h4>
              <StatusSplitBar
                segments={storeSegments}
                emptyLabel={t('analytics.no_data')}
              />
            </div>
          </div>

          <div className="space-y-5">
            <div className="flex items-center gap-4">
              <ProgressRing
                percentage={data.usersActivePercentage}
                tone="primary"
                label={t('analytics.stores_users.active_users')}
              />
              <div className="grid grid-cols-1 gap-2 flex-1">
                <MetricTile
                  label={t('analytics.stores_users.total_users')}
                  value={data.totalUsers}
                  tone="primary"
                />
                <MetricTile
                  label={t('analytics.stores_users.active_users')}
                  value={data.activeUsers}
                  tone="success"
                />
              </div>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-gray-500 mb-2">
                {t('analytics.stores_users.by_role')}
              </h4>
              <MiniBarList
                rows={data.usersByRole}
                tone="primary"
                emptyLabel={t('analytics.no_data')}
              />
            </div>
          </div>
        </div>
      )}
    </AnalyticsSectionCard>
  );
};

export default StoresUsersSection;
